import { useState } from "react";
import { Button, CircularProgress, Stack } from "@mui/material";
import { ResponsiveDialog } from "Components/Shared/Dialogs/ResponsiveDialog";
import { toast } from "sonner";
import type { SocialUserSearchDto } from "Infrastructure/Api/Api";
import { useAddChatChannelMemberMutation } from "Api/Mutations/Chat/useAddChatChannelMemberMutation";
import { Resources, useResource } from "Translations/Resources";
import {
  ChatSelectedMembers,
  type SelectedUser,
  toggleSelectedUser,
} from "Components/Chat/ChatSelectedMembers";
import { ChatUserSearch } from "Components/Chat/ChatUserSearch";
import { useSelectedUserIneligibility } from "Hooks/Chat/useSelectedUserIneligibility";
import { identityUserPublicIdFromStreamId } from "Components/Chat/chatConstants";
import { ChannelEligibility } from "Types/Chat/channelEligibility";
import { useChannelStateContext } from "stream-chat-react";

const RESOURCES = Resources.Chat.Members;

type Props = {
  isOpen: boolean;
  onClose: () => void;
  channelId: string;
};

export const AddMemberDialog = ({ isOpen, onClose, channelId }: Props) => {
  const [selectedUsers, setSelectedUsers] = useState<Array<SelectedUser>>([]);
  const { t } = useResource();
  const { channel, members } = useChannelStateContext();

  // A verified-only group (App ADR-0012) admits Full Moone personas only, whoever adds them; the
  // rule travels in the channel's custom data, mirrored from the server at creation.
  const isFullMooneRequired =
    channel.data?.eligibility === ChannelEligibility.FullMooneOnly;
  const getIneligibility = useSelectedUserIneligibility(isFullMooneRequired);
  const hasIneligibleSelection = selectedUsers.some(
    (user) => getIneligibility(user) !== undefined,
  );

  const existingMemberIdentityUserIds = Object.values(members ?? {})
    .map(({ user_id }) => user_id)
    .filter((id): id is string => Boolean(id))
    .map((id) => identityUserPublicIdFromStreamId(id));

  const { mutate: addMembers, isPending } = useAddChatChannelMemberMutation({
    onSuccess: () => {
      handleClose();
    },
    onError: () => toast.error(t(RESOURCES.AddError)),
  });

  const isAddDisabled =
    selectedUsers.length === 0 || hasIneligibleSelection || isPending;

  const handleSelectUser = (user: SocialUserSearchDto) =>
    setSelectedUsers((prev) => toggleSelectedUser(prev, user, t));

  const handleRemoveUser = (identityUserPublicID: string) => {
    setSelectedUsers((prev) =>
      prev.filter((u) => u.identityUserPublicID !== identityUserPublicID),
    );
  };

  const handleAddUsers = () => {
    if (isAddDisabled) {
      return;
    }

    addMembers({
      channelId,
      identityUserPublicIDs: selectedUsers.map(
        ({ identityUserPublicID }) => identityUserPublicID,
      ),
    });
  };

  const handleClose = () => {
    setSelectedUsers([]);
    onClose();
  };

  return (
    <ResponsiveDialog
      isOpen={isOpen}
      onClose={handleClose}
      title={t(RESOURCES.AddTitle)}
      footer={
        <Stack direction="row" justifyContent="flex-end" gap={2}>
          <Button onClick={handleClose}>{t(RESOURCES.Cancel)}</Button>
          <Button
            onClick={handleAddUsers}
            variant="contained"
            disabled={isAddDisabled}
            startIcon={isPending ? <CircularProgress size={20} /> : undefined}
            sx={{ "&.Mui-disabled": { color: "white" } }}
          >
            {t(RESOURCES.Add)}
          </Button>
        </Stack>
      }
    >
      <ChatUserSearch
        selectedUserIDs={selectedUsers.map((u) => u.identityUserPublicID)}
        existingMemberIDs={existingMemberIdentityUserIds}
        isFullMooneRequired={isFullMooneRequired}
        isMultiSelect
        onSelectUser={handleSelectUser}
      >
        <ChatSelectedMembers
          selectedUsers={selectedUsers}
          isFullMooneRequired={isFullMooneRequired}
          onRemoveUser={handleRemoveUser}
        />
      </ChatUserSearch>
    </ResponsiveDialog>
  );
};
